import type { FunctionComponent, MouseEvent } from "react";
import { useState } from "react";
import Add from "@mui/icons-material/Add";
import {
  Button,
  Divider,
  IconButton,
  Grid,
  Popover,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import CustomTooltip from "../../../components/Tooltip";
import { TextField } from "@mui/material";
import {
  parseMultiboxName,
  sortStringsLikeHuman,
} from "../../../utilities/utilityFunctions";
import { useSelector } from "../../../hooks/redux";
import { useSnackbar } from "../../../hooks/snackbar";
import { useHttpRequest } from "../../../hooks/rest";

const useStyles = makeStyles({
  divider: {
    width: "100%",
  },
  grid: {
    padding: "5px",
    maxWidth: "500px",
  },
  popover: {
    overflowY: "hidden",
    padding: "5px",
  },
});

const AddProductionButton: FunctionComponent = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const classes = useStyles();

  const hostedNetworks = useSelector((state) => state.queue.hostedNetworks);
  const hostedNetworkDetails = useSelector(
    (state) => state.queue.hostedNetworkDetails
  );

  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
    setNumAirControlPanels(1);
    setNumPrescreeners(1);
    setProductionName("");
  }

  async function handleSaveClick() {
    if (productionName.trim() !== "") {
      let tControlPanels = [];

      if (numAirControlPanels > 0) {
        for (var i = 0; i < numAirControlPanels; i++) {
          tControlPanels.push(`Endpoint ${i + 1}`);
        }
      }
      if (numPrescreeners > 0) {
        for (var j = 0; j < numPrescreeners; j++) {
          tControlPanels.push(`Prescreen ${j + 1}`);
        }
      }

      const controlPanels = tControlPanels.sort((a, b) =>
        sortStringsLikeHuman(parseMultiboxName(a), parseMultiboxName(b))
      );

      const accountName = hostedNetworks[0];

      const accountGuid = hostedNetworkDetails.filter(
        (network) => network.name === accountName
      )[0].accountGuid;

      const submissionData = {
        accountName,
        productionName,
        controlPanels: controlPanels,
        accountGuid: accountGuid,
      };

      if (accountGuid) {
        await createProductionRequest({
          body: submissionData,
        });
      } else {
        enqueueSnackbar(
          `Failed to create Production "${productionName}" - Account not found`,
          {
            hideIconVariant: true,
            persist: true,
            variant: "error",
          }
        );
      }
    }

    handleClose();
  }

  function handleCancelClick() {
    handleClose();
  }

  const [numPrescreeners, setNumPrescreeners] = useState<number>(1);
  const [numAirControlPanels, setNumAirControlPanels] = useState<number>(1);
  const [productionName, setProductionName] = useState<string>("");

  const isOpen = Boolean(anchorEl);

  const { enqueueSnackbar } = useSnackbar();

  const [createProductionRequest, createProductionRequestState] =
    useHttpRequest(`admin_proxy/productions/`, {
      method: "POST",
      onError: (e: any) => {
        enqueueSnackbar(
          `Failed to create Production "${productionName}" with error: "${
            e.message ?? "Unknown Error"
          }"`,
          {
            hideIconVariant: true,
            persist: true,
            variant: "error",
          }
        );
        handleClose();
      },
      onSuccess: () => {
        enqueueSnackbar(
          `Production "${productionName}" was successfully created. All users should be able to access it from the dropdown shortly.`,
          {
            hideIconVariant: true,
            persist: true,
            variant: "success",
          }
        );
        handleClose();
      },
    });

  return (
    <>
      <CustomTooltip title={"Click to add a new Production"}>
        <IconButton
          color={Boolean(anchorEl) ? "success" : "info"}
          disabled={createProductionRequestState.loading}
          size="medium"
          onClick={handleClick}
        >
          <Add fontSize="medium" />
        </IconButton>
      </CustomTooltip>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        classes={{
          paper: classes.popover,
        }}
        open={isOpen}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        onClose={handleClose}
      >
        <Grid className={classes.grid} container spacing={1}>
          <Grid
            alignItems="center"
            container
            item
            justifyContent="space-between"
            xs={12}
          >
            <Typography>
              <b>Create New Production</b>
            </Typography>
          </Grid>
          <Divider className={classes.divider} component="div" />
          <Grid
            alignItems="center"
            container
            item
            justifyContent="space-between"
            xs={12}
          >
            <Grid item>
              <Typography>Production Name:</Typography>
              <TextField
                autoFocus
                fullWidth
                inputProps={{ maxLength: 100 }}
                placeholder="Enter production name"
                size="small"
                value={productionName}
                variant="outlined"
                onChange={(e) => setProductionName(e.target.value)}
              />
            </Grid>

            <Grid item>
              <Typography>Prescreens:</Typography>
              <TextField
                inputProps={{ min: 0, max: 10 }}
                size="small"
                style={{ width: 80 }}
                type="number"
                value={numPrescreeners}
                variant="outlined"
                onChange={(e) => {
                  const value = Math.max(
                    0,
                    Math.min(10, Number(e.target.value))
                  );
                  setNumPrescreeners(value);
                }}
              />
            </Grid>

            <Grid item>
              <Typography>Air:</Typography>
              <TextField
                inputProps={{ min: 0, max: 10 }}
                size="small"
                style={{ width: 80 }}
                type="number"
                value={numAirControlPanels}
                variant="outlined"
                onChange={(e) => {
                  const value = Math.max(
                    0,
                    Math.min(10, Number(e.target.value))
                  );
                  setNumAirControlPanels(value);
                }}
              />
            </Grid>
          </Grid>
          <Grid container item justifyContent="space-between" spacing={1}>
            <Grid item>
              <Button
                color="error"
                disabled={createProductionRequestState.loading}
                variant="contained"
                onClick={handleCancelClick}
              >
                Cancel
              </Button>
            </Grid>
            <Grid item>
              <CustomTooltip
                title={
                  createProductionRequestState.loading
                    ? "Please wait..."
                    : productionName.trim() === ""
                    ? "Please enter a valid name for the new Production"
                    : "Click to save and initiate the new Production"
                }
              >
                <Button
                  color="success"
                  disabled={
                    productionName.trim() === "" ||
                    createProductionRequestState.loading
                  }
                  variant="contained"
                  onClick={handleSaveClick}
                >
                  Save
                </Button>
              </CustomTooltip>
            </Grid>
          </Grid>
        </Grid>
      </Popover>
    </>
  );
};

export default AddProductionButton;
